/**
 * Board Pack Dashboard - Stakeholder Visibility
 * 
 * Features:
 * - Executive summary metrics
 * - Jobs breakdown by status, site, category, month
 * - Spend analysis by site
 * - Contractor performance metrics
 * - Compliance overview
 * - Top issues requiring attention
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, BarChart3, Users, FileText, RefreshCw, Download } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({ baseURL: `${API_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('lhs_token') || localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============== COMPONENTS ==============

const MetricCard = ({ title, value, subtitle, trend, trendValue, icon: Icon, variant = 'default' }) => {
  const variantStyles = {
    default: 'border-slate-200 bg-white',
    success: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
    danger: 'border-red-200 bg-red-50',
    info: 'border-blue-200 bg-blue-50'
  };

  return (
    <Card className={`${variantStyles[variant]}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                {trendValue}
              </div>
            )}
          </div>
          {Icon && (
            <div className={`p-3 rounded-full ${variant === 'danger' ? 'bg-red-100' : variant === 'warning' ? 'bg-amber-100' : variant === 'success' ? 'bg-emerald-100' : 'bg-slate-100'}`}>
              <Icon className={`w-5 h-5 ${variant === 'danger' ? 'text-red-600' : variant === 'warning' ? 'text-amber-600' : variant === 'success' ? 'text-emerald-600' : 'text-slate-600'}`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const BarChartSimple = ({ data, title }) => {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-700">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.slice(0, 8).map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-24 text-xs text-slate-600 truncate" title={item.key}>
              {item.key}
            </div>
            <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%` }}
              />
            </div>
            <div className="w-10 text-xs font-medium text-slate-700 text-right">
              {item.value}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const IssueCard = ({ label, value, severity }) => {
  const sevStyles = {
    critical: 'bg-red-500',
    high: 'bg-amber-500',
    medium: 'bg-blue-500',
    low: 'bg-slate-400'
  };
  
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
      <span className="text-sm text-slate-700">{label}</span>
      <Badge className={`${sevStyles[severity] || sevStyles.medium} text-white`}>
        {value}
      </Badge>
    </div>
  );
};

const ContractorRow = ({ contractor }) => (
  <tr className="border-b border-slate-100 hover:bg-slate-50">
    <td className="py-3 px-4">
      <div className="font-medium text-slate-800 text-sm">{contractor.contractor_name || 'Unknown'}</div>
    </td>
    <td className="py-3 px-4 text-center">
      <Badge variant="outline">{contractor.total_jobs}</Badge>
    </td>
    <td className="py-3 px-4 text-center">
      <Badge className="bg-emerald-100 text-emerald-700">{contractor.completed_jobs}</Badge>
    </td>
    <td className="py-3 px-4 text-center">
      {contractor.overdue_jobs > 0 ? (
        <Badge className="bg-red-100 text-red-700">{contractor.overdue_jobs}</Badge>
      ) : (
        <span className="text-slate-400">-</span>
      )}
    </td>
    <td className="py-3 px-4 text-center">
      <span className="text-sm text-slate-600">{contractor.invoice_count}</span>
    </td>
    <td className="py-3 px-4 text-center">
      <span className={`text-sm font-medium ${contractor.avg_variance_pct > 5 ? 'text-red-600' : contractor.avg_variance_pct < -5 ? 'text-emerald-600' : 'text-slate-600'}`}>
        {contractor.avg_variance_pct > 0 ? '+' : ''}{contractor.avg_variance_pct}%
      </span>
    </td>
  </tr>
);

// ============== MAIN COMPONENT ==============

export default function BoardPackDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/lhs/board-pack');
      setData(res.data.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Load error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load board pack data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const topContractors = useMemo(() => {
    if (!data?.contractor_performance) return [];
    return data.contractor_performance
      .filter(c => c.total_jobs > 0)
      .slice(0, 10);
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-600">Loading Board Pack...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Error Loading Board Pack</h2>
        <p className="text-slate-600 mb-4">{error}</p>
        <Button onClick={loadData}>Retry</Button>
      </div>
    );
  }

  const summary = data?.summary || {};
  const compliance = data?.compliance || {};

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Board Pack Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Last updated: {lastRefresh?.toLocaleString() || 'Never'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <MetricCard 
          title="Total Jobs" 
          value={summary.total_jobs || 0}
          subtitle={`${summary.open_jobs || 0} open`}
          icon={FileText}
        />
        <MetricCard 
          title="Overdue Jobs" 
          value={summary.overdue_jobs || 0}
          variant={summary.overdue_jobs > 0 ? 'danger' : 'success'}
          icon={AlertTriangle}
        />
        <MetricCard 
          title="Approved Quotes" 
          value={summary.approved_quotes || 0}
          subtitle={formatCurrency(summary.total_approved_quote_value)}
          icon={CheckCircle}
          variant="info"
        />
        <MetricCard 
          title="Total Invoiced" 
          value={formatCurrency(summary.total_invoiced_value)}
          subtitle={`Validated: ${formatCurrency(summary.total_validated_invoice_value)}`}
          icon={BarChart3}
        />
        <MetricCard 
          title="Quote to Invoice Delta" 
          value={formatCurrency(Math.abs(summary.quote_to_invoice_delta || 0))}
          trend={summary.quote_to_invoice_delta > 0 ? 'up' : 'down'}
          trendValue={summary.quote_to_invoice_delta > 0 ? 'Over budget' : 'Under budget'}
          variant={summary.quote_to_invoice_delta > 1000 ? 'warning' : 'default'}
        />
        <MetricCard 
          title="Blocked Invoices" 
          value={summary.blocked_invoices || 0}
          variant={summary.blocked_invoices > 0 ? 'warning' : 'success'}
          icon={AlertTriangle}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-white border">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Contractor Performance</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="issues">Top Issues</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <BarChartSimple data={data?.jobs_by_status} title="Jobs by Status" />
            <BarChartSimple data={data?.jobs_by_site} title="Jobs by Site" />
            <BarChartSimple data={data?.jobs_by_category} title="Jobs by Category" />
            <BarChartSimple data={data?.jobs_by_month} title="Jobs by Month" />
            <BarChartSimple data={data?.spend_by_site} title="Spend by Site (£)" />
          </div>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Top Contractors by Job Volume
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topContractors.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 text-left">
                        <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Contractor</th>
                        <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase text-center">Total Jobs</th>
                        <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase text-center">Completed</th>
                        <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase text-center">Overdue</th>
                        <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase text-center">Invoices</th>
                        <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase text-center">Avg Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topContractors.map((c, idx) => (
                        <ContractorRow key={idx} contractor={c} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No contractor data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard 
              title="Total Certificates" 
              value={compliance.total_certificates || 0}
              icon={FileText}
            />
            <MetricCard 
              title="Expired" 
              value={compliance.expired || 0}
              variant={compliance.expired > 0 ? 'danger' : 'success'}
              icon={AlertTriangle}
            />
            <MetricCard 
              title="Expiring Soon (<30d)" 
              value={compliance.expiring_soon || 0}
              variant={compliance.expiring_soon > 0 ? 'warning' : 'success'}
              icon={AlertTriangle}
            />
          </div>
        </TabsContent>

        {/* Issues Tab */}
        <TabsContent value="issues">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Top Issues Requiring Attention
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(data?.top_issues || []).map((issue, idx) => (
                <IssueCard 
                  key={idx} 
                  label={issue.label} 
                  value={issue.value}
                  severity={issue.value > 5 ? 'critical' : issue.value > 2 ? 'high' : issue.value > 0 ? 'medium' : 'low'}
                />
              ))}
              {(!data?.top_issues || data.top_issues.length === 0) && (
                <p className="text-slate-500 text-center py-8">No issues detected</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
